import { Col, DatePicker, Form, FormInstance, Input, Select } from "antd-v5";
import dayjs, { Dayjs } from "dayjs";
import { isArray } from "lodash";
import moment from "moment";
import React from "react";
import { RiskRegisterReviewerResponseDto } from "types/api/riskRegisterReviewerResponseDto";
import { UserInfoProjectorImpl } from "types/api/userInfoProjectorImpl";
import { ArmorcodeCustomField, RiskAssessmentTypes } from "types/tenantConfig";
import { parseSelectOptions } from "utils/formUtils";
import {
  STATUS_DRAFT,
  STATUS_REJECTED,
} from "views/Inventory/Project/Constants";

export type FieldRenderMap = {
  [key in RiskAssessmentTypes]: (
    props: ArmorcodeCustomField,
  ) => React.ReactNode;
};

export type ExceptionData = {
  status: string;
  riskAssessmentFields: { id: string; values: string[] | Dayjs[] }[];
  id: number;
  startDate: number;
  endDate: number;
  approveByDate: number;
  name: string;
  approvalPendingAtUserId: number;
  approvalPendingAtUser: UserInfoProjectorImpl;
  customWorkflow: any;
  inheritedWorkFlow: any;
  scope: any;
  nextReviewer?: RiskRegisterReviewerResponseDto;
};

export const FIELD_RENDER: FieldRenderMap = {
  [RiskAssessmentTypes["Text-Short"]]: () => <Input />,
  [RiskAssessmentTypes["Text-Long"]]: () => <Input.TextArea />,
  [RiskAssessmentTypes.Date]: () => <DatePicker className="w-100" />,
  [RiskAssessmentTypes["Single-Select"]]: (props: ArmorcodeCustomField) => (
    <Select
      popupMatchSelectWidth={false}
      options={parseSelectOptions(props.values)}
      value={props.values ? props.values[0] : undefined}
    />
  ),
  [RiskAssessmentTypes["Multi-Select"]]: (props: ArmorcodeCustomField) => (
    <Select
      mode="multiple"
      popupMatchSelectWidth={false}
      options={parseSelectOptions(props.values)}
      value={props.values ? props.values : undefined}
    />
  ),
};

export const isDraftOrRejected = (status: string) =>
  [STATUS_DRAFT, STATUS_REJECTED].indexOf(status) !== -1;

export const getFormRiskAssessmentFields = (
  riskAssessmentFields: ArmorcodeCustomField[],
  colSpan?: number,
) => {
  return riskAssessmentFields.map((field) => {
    const getComponent = FIELD_RENDER[field.type];
    if (!getComponent) {
      return null;
    }

    return (
      <Col
        key={field.id}
        span={
          colSpan
            ? colSpan
            : [RiskAssessmentTypes["Text-Long"]].indexOf(field.type) !== -1
              ? 24
              : 9
        }
      >
        <Form.Item
          name={field.id}
          key={field.id}
          label={field.label}
          rules={[
            {
              required: field.required,
              message: `${field.label} is required`,
            },
          ]}
        >
          {getComponent(field)}
        </Form.Item>
      </Col>
    );
  });
};

export const getDisplayValue = (
  field: ArmorcodeCustomField,
  form: FormInstance,
) => {
  let displayValue = form.getFieldValue(field.id);

  if (field.type === RiskAssessmentTypes.Date && displayValue) {
    displayValue = dayjs(displayValue).format("MMMM D, YYYY");
  } else if (field.type === RiskAssessmentTypes["Multi-Select"]) {
    displayValue = isArray(displayValue) ? displayValue.join(", ") : undefined;
  }

  return displayValue;
};

export function isTodayInRange(endDateInp: number) {
  const endDate = moment(endDateInp);

  const startDate = endDate.clone().subtract(30, "days");
  const endDateRange = endDate.clone().add(5, "days");

  const today = moment();

  return today.isBetween(startDate, endDateRange, "day", "[]");
}
